/**
 * VersionComplianceAgGridHook
 *
 * Grid for /admin/version-compliance. Uses AG Grid's server-side row model
 * because the compliance status shown per row (up_to_date / outdated /
 * never_received / no_target) is now computed in SQL — see
 * TmsCore.TerminalManagement.VersionCompliance — so filtering by status and
 * paginating both happen against the database rather than a fully-loaded
 * terminal list.
 */

import { mountServerSideGrid } from "../base/ServerSideAgGridHook";

const PAGE_SIZE = 25;

const STATUS_BADGE = {
  up_to_date: { icon: "✅", bg: "#DCFCE7", color: "#166534" },
  outdated: { icon: "⚠️", bg: "#FEF3C7", color: "#92400E" },
  never_received: { icon: "❌", bg: "#FEE2E2", color: "#991B1B" },
  no_target: { icon: "🔵", bg: "#EFF6FF", color: "#60A5FA" },
};

const OVERALL_LABEL = {
  up_to_date: "Compliant",
  outdated: "Outdated",
  never_received: "Never pushed",
  no_target: "No target",
};

const slotBadge = (status, target) => {
  const meta = STATUS_BADGE[status] || STATUS_BADGE.no_target;
  const label = status === "never_received" ? "nil" : status === "no_target" ? "—" : target || "—";
  return `<span style="background:${meta.bg};color:${meta.color};padding:1px 8px;border-radius:9999px;font-size:11px;font-weight:600;white-space:nowrap;">${meta.icon} ${label}</span>`;
};

const overallBadge = (status) => {
  const meta = STATUS_BADGE[status] || STATUS_BADGE.no_target;
  return `<span style="background:${meta.bg};color:${meta.color};padding:2px 10px;border-radius:9999px;font-size:12px;font-weight:700;white-space:nowrap;">${meta.icon} ${OVERALL_LABEL[status] || status}</span>`;
};

const reportedCell = (params) =>
  params.value == null
    ? '<span style="color:var(--ag-secondary-foreground-color);font-family:monospace;font-size:12px;">—</span>'
    : `<span style="font-family:monospace;font-size:12px;color:var(--ag-data-color);">${params.value}</span>`;

const VersionComplianceAgGridHook = {
  mounted() {
    if (!window.agGrid || !window.agGrid.createGrid) {
      console.error("VersionComplianceAgGridHook: agGrid not found on window");
      return;
    }

    const self = this;
    const canRemediate = this.el.dataset.canRemediate === "true";

    const actionsRenderer = (params) => {
      if (!params.data || !canRemediate || !params.data.non_compliant) return "";
      const btn = document.createElement("button");
      btn.textContent = "Re-push";
      btn.style.cssText = "color:#B45309;font-size:12px;font-weight:600;cursor:pointer;background:none;border:none;padding:0;";
      btn.addEventListener("click", (e) => {
        e.stopPropagation();
        self.pushEvent("repush_terminal", {
          serial: params.data.serial_number,
          vendor: params.data.vendor,
          model: params.data.model,
        });
      });
      return btn;
    };

    const slotGroup = (headerName, reportedField, targetField, statusField) => ({
      headerName,
      children: [
        {
          headerName: "Reported",
          field: reportedField,
          width: 110,
          sortable: false,
          cellRenderer: reportedCell,
        },
        {
          headerName: "Target",
          colId: `${statusField}_target`,
          width: 130,
          sortable: false,
          cellRenderer: (params) => slotBadge(params.data[statusField], params.data[targetField]),
        },
      ],
    });

    const columnDefs = [
      {
        headerName: "Terminal",
        field: "serial_number",
        width: 170,
        cellRenderer: (params) => {
          if (!params.data) return "";
          return `<div>
            <div style="font-family:monospace;font-size:12px;font-weight:500;color:var(--ag-data-color);">${params.value || ""}</div>
            <div style="font-size:11px;color:var(--ag-secondary-foreground-color);">${params.data.terminal_status || ""}</div>
          </div>`;
        },
      },
      {
        headerName: "Vendor / Model",
        colId: "vendor_model",
        width: 160,
        sortable: false,
        cellRenderer: (params) => {
          if (!params.data) return "";
          return `<span style="font-weight:500;">${params.data.vendor || ""}</span> / <span style="color:var(--ag-secondary-foreground-color);">${params.data.model || ""}</span>`;
        },
      },
      slotGroup("Parameter Config", "param_reported", "param_target", "param_status"),
      slotGroup("EMV Config", "emv_reported", "emv_target", "emv_status"),
      slotGroup("Keys Config", "keys_reported", "keys_target", "keys_status"),
      {
        headerName: "Overall",
        field: "overall_status",
        width: 150,
        cellRenderer: (params) => overallBadge(params.value),
      },
      {
        headerName: "Actions",
        colId: "actions",
        pinned: "right",
        width: 100,
        sortable: false,
        filter: false,
        floatingFilter: false,
        resizable: false,
        cellRenderer: actionsRenderer,
      },
    ];

    this.gridApi = mountServerSideGrid(this, {
      columnDefs,
      rowsEvent: "version_compliance:rows",
      refreshEvent: "version_compliance:refresh",
      pageSize: PAGE_SIZE,
    });
  },

  destroyed() {
    if (this.gridApi) {
      this.gridApi.destroy();
      this.gridApi = null;
    }
  },
};

export default VersionComplianceAgGridHook;
